import { useEffect, useMemo, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import PrintLogoHeader from "@/components/PrintLogoHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  Printer,
  Play,
  Pause,
  RotateCcw,
  XCircle,
  MessageSquare,
  PhoneCall,
  ShieldAlert,
  CalendarCheck,
} from "lucide-react";
import DemoQRCode from "@/components/DemoQRCode";
import api from "@/lib/api";

// Single accent color — navy → gold treatment. Every step uses the same
// hue so the eye tracks left-to-right instead of hunting through a rainbow.
const ACCENT = "bg-navy text-white";
const ACCENT_RING = "ring-[hsl(var(--gold))]";

const buildStops = (s) => {
  const d1 = s?.stage_1_discount ?? 30;
  const d2 = s?.stage_2_discount ?? 40;
  const day1 = s?.stage_1_days ?? 7;
  const day2 = s?.stage_2_days ?? 15;
  const day60 = s?.comeback_day_60_days ?? 60;
  const day90 = s?.comeback_day_90_days ?? 90;
  return [
    {
      day: "Day 0",
      title: "Customer declines a repair",
      body: "Advisor writes it up. Customer says \"not today.\" Under most vendors, the story ends here.",
      Icon: XCircle,
    },
    {
      day: `Days ${day1} & ${day2}`,
      title: "Offer texts fire — from your schedule",
      body: `Day ${day1} at ${d1}% off, Day ${day2} at ${d2}% off. Unique coupon per RO. Tap-to-schedule link. Zero advisor clicks.`,
      Icon: MessageSquare,
    },
    {
      day: "Any time",
      title: "Customer books an appointment",
      body: "Two taps and they're on your schedule. Coupon scanned at the desk. Recovered dollars land on your dashboard.",
      Icon: CalendarCheck,
    },
    {
      day: `Day ${day60}`,
      title: "Advisor call task drops",
      body: "If two texts don't convert, a task lands on your advisor's queue — a human call, in your voice, on your timing.",
      Icon: PhoneCall,
    },
    {
      day: `Day ${day90}`,
      title: "Manager relationship call",
      body: "Service Manager gets a final task — \"we care about you as a customer.\" One conversation your team would never have made on their own.",
      Icon: ShieldAlert,
    },
  ];
};

const STEP_MS = 2600;

export default function CustomerJourneyPage() {
  const [settings, setSettings] = useState(null);
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const STOPS = useMemo(() => buildStops(settings), [settings]);

  useEffect(() => {
    if (!playing) return;
    const t = setTimeout(() => {
      setActive((i) => (i + 1) % STOPS.length);
    }, STEP_MS);
    return () => clearTimeout(t);
  }, [active, playing, STOPS.length]);

  const progressPct = useMemo(
    () => Math.min(100, ((active + 1) / STOPS.length) * 100),
    [active, STOPS.length]
  );

  return (
    <DashboardShell>
      <PrintLogoHeader docTitle="Customer Journey" />

      <div className="mb-6 flex items-start justify-between gap-4 no-print">
        <div>
          <div className="font-mono-label text-muted-foreground">Sales Handout · Animated</div>
          <h1 className="font-display text-4xl font-bold tracking-tighter mt-1">
            What happens to your customer
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Five steps from &quot;not today&quot; to a paid RO — all on the schedule you set in Settings.
            Hand the printed version to the dealer principal after they&apos;ve watched it move.
          </p>
        </div>
        <div className="flex gap-2 no-print">
          <Button
            variant="outline"
            onClick={() => setPlaying((p) => !p)}
            data-testid="journey-play-toggle"
          >
            {playing ? (
              <>
                <Pause className="w-4 h-4 mr-2" strokeWidth={2} />
                Pause
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" strokeWidth={2} />
                Play
              </>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setActive(0);
              setPlaying(true);
            }}
            data-testid="journey-replay"
          >
            <RotateCcw className="w-4 h-4 mr-2" strokeWidth={2} />
            Replay
          </Button>
          <Button onClick={() => window.print()} data-testid="print-journey-btn">
            <Printer className="w-4 h-4 mr-2" strokeWidth={2} />
            Print
          </Button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="max-w-4xl mx-auto mb-6 no-print" data-testid="journey-progress">
        <div className="flex items-center justify-between font-mono-label text-[0.6rem] text-muted-foreground mb-1.5">
          <span>Step {active + 1} of {STOPS.length}</span>
          <span>{STOPS[active].day}</span>
        </div>
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <motion.div
            className="h-full bg-[hsl(var(--gold))]"
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          />
        </div>
      </div>

      {/* Animated 5-step vertical timeline (screen) */}
      <div className="max-w-4xl mx-auto relative no-print" data-testid="journey-timeline">
        {/* Rail — single accent */}
        <div className="absolute left-[3.25rem] top-4 bottom-4 w-0.5 bg-navy/20" />
        <div className="space-y-3">
          {STOPS.map((s, i) => {
            const isActive = i === active;
            const isPast = i < active;
            return (
              <motion.button
                key={i}
                type="button"
                onClick={() => {
                  setPlaying(false);
                  setActive(i);
                }}
                data-testid={`journey-step-${i}`}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                className={`w-full text-left flex items-stretch gap-4 group focus:outline-none ${
                  isActive ? "" : "opacity-70 hover:opacity-100"
                }`}
              >
                {/* Icon dot with glow when active — single accent */}
                <div className="relative shrink-0 w-[6.5rem] flex flex-col items-center pt-2">
                  <motion.div
                    className={`relative w-14 h-14 rounded-full ${ACCENT} grid place-items-center shadow-lg z-10`}
                    animate={
                      isActive
                        ? { scale: [1, 1.08, 1] }
                        : { scale: 1 }
                    }
                    transition={
                      isActive
                        ? { duration: 1.4, repeat: Infinity, ease: "easeInOut" }
                        : { duration: 0.3 }
                    }
                  >
                    <s.Icon className="w-6 h-6" strokeWidth={2.2} />
                    {isActive && (
                      <motion.span
                        className={`absolute inset-0 rounded-full ring-4 ${ACCENT_RING}`}
                        initial={{ opacity: 0.8, scale: 1 }}
                        animate={{ opacity: 0, scale: 1.9 }}
                        transition={{ duration: 1.4, repeat: Infinity, ease: "easeOut" }}
                      />
                    )}
                  </motion.div>
                  <div className="font-mono-label text-[0.55rem] tracking-widest text-muted-foreground mt-2 text-center leading-tight">
                    {s.day}
                  </div>
                </div>

                {/* Card */}
                <Card
                  className={`flex-1 p-4 border transition-all ${
                    isActive
                      ? "border-[hsl(var(--gold))] shadow-lg"
                      : isPast
                      ? "border-navy/40 bg-navy/5"
                      : "border-border"
                  }`}
                >
                  <div className="font-display font-bold text-base tracking-tight flex items-center gap-2">
                    {s.title}
                    {isPast && (
                      <span className="text-[0.55rem] font-mono-label text-navy bg-navy/10 px-1.5 py-0.5 rounded">
                        ✓ DONE
                      </span>
                    )}
                    {isActive && (
                      <motion.span
                        className="text-[0.55rem] font-mono-label text-[hsl(var(--gold))] bg-[hsl(var(--gold))]/10 px-1.5 py-0.5 rounded"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        NOW
                      </motion.span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.body}</div>
                </Card>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Print-only static 5-step timeline */}
      <div className="max-w-3xl mx-auto space-y-2 hidden print:block">
        {STOPS.map((s, i) => (
          <Card key={i} className="p-0 overflow-hidden border-border">
            <div className="flex items-stretch">
              <div className={`w-24 shrink-0 ${ACCENT} grid place-items-center p-3`}>
                <div className="text-center">
                  <s.Icon className="w-5 h-5 mx-auto" strokeWidth={2} />
                  <div className="font-mono-label text-white/85 text-[0.55rem] mt-1 tracking-widest">
                    {s.day}
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4">
                <div className="font-display font-bold text-base tracking-tight">{s.title}</div>
                <div className="text-sm text-muted-foreground mt-1 leading-relaxed">{s.body}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Bottom close card */}
      <div className="max-w-4xl mx-auto mt-8 pt-4 border-t border-border">
        <Card
          className="p-5 border-[hsl(var(--gold))]/40 !bg-navy text-white"
          data-testid="journey-close"
        >
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <div className="flex-1">
              <div className="font-mono-label text-[hsl(var(--gold-light))] text-[0.6rem]">
                The Bottom Line
              </div>
              <div className="font-display font-bold text-lg mt-1 text-white">
                Zero advisor clicks. Zero customer sign-ups. Zero minutes of your team&apos;s time.
              </div>
              <div className="text-sm text-white/85 mt-2">
                Every step above runs on the schedule you control in Settings. Decline Drive is the
                plumbing between &quot;not today&quot; and &quot;paid RO&quot; — it never sleeps.
              </div>
            </div>
            <DemoQRCode size={110} caption="Scan · see it in action" />
          </div>
        </Card>
      </div>
    </DashboardShell>
  );
}
